import type { Info } from '@studio/models/gamepad';
import type { Magnitude } from '@studio/models/ptz';
import type { Command, Directive as Directive_ } from '#~/command/index.js';

/** Gamepad action (subset of `Command`). */
export type Action =
  | Command.FOCUS_FAR
  | Command.FOCUS_MODE
  | Command.FOCUS_NEAR
  | Command.FOCUS_STOP
  | Command.PTZ_PAN
  | Command.PTZ_TILT
  | Command.PTZ_ZOOM
  | Command.PTZ_ZOOM_IN
  | Command.PTZ_ZOOM_OUT
  | Command.QUICK_STOP
  | Command.TEMPO_PT_TOGGLE
  | Command.VIDEO_ARRANGEMENT
  | Command.VIDEO_SOURCE_NEXT
  | Command.VIDEO_SOURCE_PREV
  | Command.CONTROL_PRESET_ADD
  | Command.PT_SPEED_UP
  | Command.PT_SPEED_DOWN;

/** Gamepad ID collection. */
export type Collection = readonly Gamepad['id'][];

/** Gamepad directive. */
export type Directive = Directive$Axis | Directive$Button;

/** Axis directive. */
export type Directive$Axis = Directive_<Action, { gamepadId: Gamepad['id']; value: Magnitude }>;

/** Button directive. */
export type Directive$Button = Directive_<Action, { gamepadId: Gamepad['id'] }>;

/**
 * Opaque string mapping of a gamepad input, whether a button press or axis actuation. While
 * irrelevant, the format of the fingerprint consists of two colon separated the form `a:b` where:
 *
 * - `a` is the number of the actuated axis or `x` if no axis was actuated.
 * - `b` is the number of the depressed button or `x` if no button was pressed.
 *
 * For example:
 *
 * - `x:7` - indicates that the 7th button was pressed.
 * - `2:x` - indicates that the 2nd axis was actuated.
 */
export type Fingerprint = string;

/** Mapping formula. */
export type Formula = Record<Fingerprint, Action>;

/** Gamepad axis/button input. */
export type Input = Readonly<{ axis: number } | { button: number }>;

/** Gamepad button/axis layout. */
export type Layout = Readonly<{ [key in Action]?: Input | ReadonlyArray<Input> | false }>;

/** Gamepad profile. */
export type Profile = Readonly<{
  console: Console;
  formula: Formula;
  gamepadId: Gamepad['id'];
  info: Info;
}>;

/** Profile collection. */
export type Profiles = Record<Profile['gamepadId'], Profile>;

/** Gamepad console. */
export enum Console {
  Generic = 'generic',
  PlayStation = 'playstation',
  Switch = 'switch',
  Xbox = 'xbox'
}
